/**
 * The referrer the site's own GA4 pushes carry (page_referrer).
 *
 * Sign-in and payment pages hand the visitor back to us, but they are a step
 * in our own flow, not a traffic source: Google sign-in (accounts.google.com,
 * and Google's country domains accounts.google.de / .nl / .ca / .ru … that
 * appear when it sets its cookies on the way back, plus accounts.youtube.com),
 * Apple sign-in (appleid.apple.com) and Stripe (checkout., pay., billing.,
 * buy., hooks. … .stripe.com).
 *
 * The browser keeps such a referrer for the whole life of the document: SPA
 * navigations, a reload, a tab restored the next day. Every push from that
 * tab would present the hop as the referrer, and a session that starts there
 * after 30 idle minutes would start as "accounts.google.de / referral". GA4's
 * "List unwanted referrals" marks matching hits ir=1, but only for the hosts
 * listed there (on 07.10 that was accounts.google.com, not .de or .nl). So
 * for these hosts the site sends its own origin instead. GA4 never counts its
 * own domain as a referral, whatever the list says, and GTM needs no change:
 * «GA4 – event settings» and the page_view tag already map page_referrer.
 *
 * The Google tag's own hits (user_engagement, scroll, click, file_download)
 * still read document.referrer; the unwanted-referrals list covers those
 * (GTM_NOTES.md).
 */
import { redactReferrer } from "./redact";

const HOP_HOST_RE =
    /^(?:accounts\.google\.[a-z]{2,3}(?:\.[a-z]{2})?|accounts\.youtube\.com|appleid\.apple\.com|(?:[a-z0-9-]+\.)*stripe\.com)$/;

/** accounts.google.<any country>, accounts.youtube.com, appleid.apple.com, *.stripe.com. */
export const isHopReferrerHost = (host: string): boolean =>
    HOP_HOST_RE.test(
        String(host || "")
            .toLowerCase()
            .replace(/\.$/, ""),
    );

/**
 * page_referrer for a push: our own origin after a sign-in / payment hop,
 * otherwise the referrer redacted (redact.ts). "" when there is none.
 */
export const analyticsReferrer = (href: string): string => {
    if (!href) return "";
    let host = "";
    try {
        host = new URL(href).hostname;
    } catch {
        return "";
    }
    if (!isHopReferrerHost(host)) return redactReferrer(href);
    try {
        return `${window.location.origin}/`;
    } catch {
        return "";
    }
};
